import * as Sentry from '@sentry/react';
import { DELETE, MPOST, MPUT } from 'common/helpers.ts';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';
import type { APP_NAME } from 'types/misc.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { ILocked, IRefresh, IUnlocked } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { DeleteDialog } from '#admin/component/DeleteDialog.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

export type DialogConfig = {
  action: 'edit' | 'delete' | 'reset-credentials' | 'lock' | 'unlock';
  redirect?: string;
};

type Props = {
  user: User;
  config: DialogConfig;
  onSave?: () => void;
  onClose?: () => void;
  isOpen: boolean;
};

export const UserDialog: React.FC<Props> = ({ user, config, onSave, onClose, isOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { action, redirect } = config;
  const deleteSwr = useSWRMutation([`/user/${user.id}`], DELETE);
  const lockSwr = useSWRMutation(`/user/${user.id}`, MPUT);
  const resetSwr = useSWRMutation('/password/request', MPOST);

  const loading = deleteSwr.isMutating || resetSwr.isMutating || lockSwr.isMutating;
  const error = deleteSwr.error || resetSwr.error || lockSwr.error;
  const resetPasswordApp: APP_NAME = user.role.toUpperCase() === 'DRIVER' ? 'driver' : 'admin';

  const onConfirm = async () => {
    try {
      switch (action) {
        case 'delete':
          await deleteSwr.trigger();
          break;
        case 'reset-credentials':
          await resetSwr.trigger({ email: user.email, app: resetPasswordApp });
          break;
        case 'lock':
          await lockSwr.trigger({ isActive: false });
          break;
        case 'unlock':
          await lockSwr.trigger({ isActive: true });
          break;
      }
      if (onSave) onSave();
      if (redirect) navigate(redirect);
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  if (action === 'delete') {
    return (
      <DeleteDialog
        key={user.id}
        isOpen={isOpen}
        onClose={onClose ?? (() => {})}
        onConfirm={onConfirm}
        title={t('dialogs.user.deleteTitle')}
        message={error ? getTranslatedError(error, t) || t('dialogs.user.unknownError') : t('dialogs.user.confirmMessage')}
        loading={loading}
      />
    );
  }

  return (
    <Modal key={user.id} isOpen={isOpen} onClose={onClose}>
      <div className="flex w-[450px] flex-col gap-8">
        <div className="flex grow items-center justify-center">
          {action === 'reset-credentials' && <IRefresh size={128} className="blue rounded-full bg-blue-100 p-2" />}
          {action === 'lock' && <ILocked size={128} className="blue rounded-full bg-blue-100 p-2" />}
          {action === 'unlock' && <IUnlocked size={128} className="blue rounded-full bg-blue-100 p-2" />}
        </div>
        {action === 'lock' && <h4 className="text-center">{t('dialogs.user.lockTitle')}</h4>}
        {action === 'unlock' && <h4 className="text-center">{t('dialogs.user.unlockTitle')}</h4>}
        {action === 'reset-credentials' && (
          <>
            <h4 className="text-center">{t('dialogs.user.resetTitle')}</h4>
            <p className="text-center">{t('dialogs.user.resetMessage')}</p>
          </>
        )}
        {!error && <p className="text-center">{t('dialogs.user.confirmMessage')}</p>}
        {error && <p className="red text-center">{getTranslatedError(error, t) || t('dialogs.user.unknownError')}</p>}
        <div className="mt-6 flex w-full gap-4">
          <Button onClick={onClose} className="blue-outlined">
            {t('actions.back')}
          </Button>
          <Button type="submit" loading={loading} onClick={onConfirm} className="blue">
            {t('actions.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
